import { BOB_LEVELS } from '../../../packages/domain/src/67-bob-levels.mjs';
import { getBobWorld } from '../../../packages/domain/src/73-bob-worlds.mjs';
import { getBobBackdropUrl } from './74-bob-renderer.mjs';

let serial = 0;

/** An inline arcade atlas: twenty worlds show earned progression. Selection
 * is explicit and never launches a run; the player still starts its sequence.
 * Native buttons retain keyboard/touch semantics without a modal or select. */
export function createBobWorldPicker(ctx, { onChange = () => {} } = {}) {
  const root = ctx.e('div', 'bob-world-picker');
  const opener = ctx.e('button', 'bob-world-open');
  opener.type = 'button';
  const thumb = ctx.e('img', 'bob-world-thumb');
  thumb.alt = '';
  const caption = ctx.e('span', 'bob-world-caption');
  const kicker = ctx.e('span', 'bob-world-kicker', 'WORLDS');
  const current = ctx.e('strong', 'bob-world-current');
  caption.append(kicker, current);
  const arrow = ctx.e('span', 'bob-world-arrow', '▦');
  if (ctx.icon) arrow.replaceChildren(ctx.icon('globe'));
  arrow.setAttribute('aria-hidden', 'true');
  opener.append(thumb, caption, arrow);
  const panel = ctx.e('section', 'bob-world-panel');
  panel.id = `bob-world-atlas-${++serial}`;
  panel.hidden = true;
  panel.setAttribute('aria-label', 'Choose your world');
  opener.setAttribute('aria-controls', panel.id);
  opener.setAttribute('aria-expanded', 'false');
  const header = ctx.e('header', 'bob-world-heading');
  const heading = ctx.e('div');
  heading.append(
    ctx.e('span', 'bob-world-kicker', 'THE WORLD TOUR'),
    ctx.e('h3', '', 'Where to, Bob?'),
  );
  const close = ctx.e('button', 'bob-world-close', '×');
  close.type = 'button';
  close.setAttribute('aria-label', 'Close world selection');
  header.append(heading, close);
  const grid = ctx.e('div', 'bob-select');
  grid.setAttribute('role', 'group');
  grid.setAttribute('aria-label', 'Twenty worlds to discover');
  const note = ctx.e('p', 'bob-world-note', 'Finish each world to open the next adventure.');
  panel.append(header, note, grid);
  root.append(opener, panel);
  const listeners = [];
  const listen = (node, type, fn) => {
    node.addEventListener(type, fn);
    listeners.push([node, type, fn]);
  };
  let value = '1',
    unlocked = 1;
  const cards = BOB_LEVELS.map((level) => {
    const card = ctx.e('button', 'bob-world-card');
    card.type = 'button';
    card.value = String(level.id);
    const image = ctx.e('img');
    image.src = getBobBackdropUrl(level.id);
    image.alt = '';
    image.loading = 'lazy';
    image.decoding = 'async';
    image.width = 160;
    image.height = 110;
    const number = ctx.e('span', 'bob-world-number', String(level.id).padStart(2, '0'));
    const label = ctx.e('span', 'bob-world-card-label');
    label.append(ctx.e('strong', '', level.name), ctx.e('span', '', getBobWorld(level.id).name));
    const status = ctx.e('span', 'bob-world-card-status');
    card.append(image, number, label, status);
    card.setAttribute('aria-label', `World ${level.id}: ${level.name}`);
    grid.append(card);
    listen(card, 'click', () => {
      if (level.id > unlocked) return;
      paint(String(level.id));
      dismiss();
      onChange(value);
    });
    return card;
  });
  function paint(next) {
    const level = BOB_LEVELS.find((entry) => String(entry.id) === String(next));
    if (!level || level.id > unlocked) return;
    value = String(level.id);
    current.textContent = `${String(level.id).padStart(2, '0')} · ${level.name}`;
    thumb.src = getBobBackdropUrl(level.id);
    opener.setAttribute('aria-label', `Choose world. Current: ${level.name}`);
    note.textContent = `${unlocked} / 20 WORLDS OPEN · Finish each world to unlock the next.`;
    for (const card of cards) {
      const id = Number(card.value),
        locked = id > unlocked;
      card.disabled = locked;
      card.setAttribute('aria-pressed', String(card.value === value));
      card.setAttribute(
        'aria-label',
        `World ${id}: ${BOB_LEVELS[id - 1].name}${locked ? `. Locked. Finish World ${id - 1} first.` : ''}`,
      );
      card.children[3].textContent = locked
        ? `🔒 Finish World ${id - 1}`
        : id < unlocked
          ? '★ COMPLETED'
          : '▶ READY TO PLAY';
    }
  }
  function dismiss() {
    const wasOpen = !panel.hidden;
    panel.hidden = true;
    opener.setAttribute('aria-expanded', 'false');
    if (wasOpen) opener.focus?.({ preventScroll: true });
  }
  listen(opener, 'click', () => {
    if (!panel.hidden) return dismiss();
    panel.hidden = false;
    opener.setAttribute('aria-expanded', 'true');
    cards.find((card) => card.value === value)?.focus?.({ preventScroll: true });
  });
  listen(close, 'click', dismiss);
  listen(panel, 'keydown', (event) => {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    dismiss();
  });
  paint(value);
  return {
    root,
    get value() {
      return value;
    },
    set value(next) {
      paint(next);
    },
    unlock(maximum) {
      if (!Number.isSafeInteger(maximum) || maximum < 1 || maximum > 20) return;
      unlocked = maximum;
      paint(String(Math.min(Number(value), unlocked)));
    },
    close: dismiss,
    dispose() {
      for (const [node, type, fn] of listeners) node.removeEventListener(type, fn);
      root.remove();
    },
  };
}
